import { subYears } from "date-fns";
import { Checkbox, DateInput } from "flicket-ui";
import * as Yup from "yup";

import { DatePicker, Select } from "~components";
import {
  Gender,
  OrderQuery,
  TicketQuery,
  VenueAddressFragment,
} from "~graphql/sdk";
import { DOBDateInputSchema } from "~lib/helpers/DOBDateInputSchema";
import { formatEventDate } from "~lib/helpers/formatDate";

export const genderOptions = [
  {
    value: Gender.Female,
    label: "Female",
  },
  {
    value: Gender.Male,
    label: "Male",
  },
  {
    value: Gender.Other,
    label: "Other",
  },
];

export const formKeys = {
  firstName: {
    label: "First name",
  },
  lastName: {
    label: "Last name",
  },
  email: {
    label: "E-mail",
  },
  phoneNumber: {
    label: "Phone number",
  },
  birthDate: {
    label: "Date of birth",
    as: DateInput,
  },
  gender: {
    label: "Gender",
    as: Select,
  },
  companyName: {
    label: "Company",
  },
  postalCode: {
    label: "Postalcode",
  },
  licensePlate: {
    label: "License plate number",
  },
  memberSince: {
    label: "Member since",
    as: DatePicker,
  },
  membershipNumber: {
    label: "Membership number",
  },
  comments: {
    label: "Comments",
  },
  dietaryRequirements: {
    label: "Dietary requirements",
  },
  vaccinePass: {
    label: "Vaccine Pass",
  },
  usableForEmailMarketing: {
    label: "Opt in to email marketing",
    as: Checkbox,
  },
  usableForSMSMarketing: {
    label: "Opt in to text marketing",
    as: Checkbox,
  },
  lengthOfStay: {
    label: "Length of stay",
  },
  emergencyName: {
    label: "Emergency contact name",
  },
  emergencyContact: {
    label: "Emergency contact number",
  },
  emergencyContactRelationship: {
    label: "Relationship to emergency contact",
  },
  dunedinResidentialAddress: {
    label: "Residential address in Dunedin",
  },
  studentId: {
    label: "Student ID",
  },
};

export const initialValues = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  birthDate,
  gender,
  postalCode,
  companyName,
  licensePlate,
  comments,
  membershipNumber,
  dietaryRequirements,
  memberSince,
  usableForEmailMarketing,
  usableForSMSMarketing,
  lengthOfStay,
  emergencyContact,
  emergencyContactRelationship,
  dunedinResidentialAddress,
  emergencyName,
  studentId,
  customFields,
}:
  | Partial<OrderQuery["order"]["tickets"][number]>
  | Partial<TicketQuery["ticket"]>) => ({
  firstName: firstName ?? "",
  lastName: lastName ?? "",
  email: email ?? "",
  phoneNumber: phoneNumber ?? "",
  birthDate: birthDate ?? "",
  gender: genderOptions.find(({ value }) => value === gender) ?? "",
  postalCode: postalCode ?? "",
  companyName: companyName ?? "",
  membershipNumber: membershipNumber ?? "",
  comments: comments ?? "",
  dietaryRequirements: dietaryRequirements ?? "",
  licensePlate: licensePlate ?? "",
  memberSince: memberSince ?? "",
  // should never reach the fallback here but just in case,
  // let's comply with privacy act and use default of false
  usableForEmailMarketing: usableForEmailMarketing ?? false,
  usableForSMSMarketing: usableForSMSMarketing ?? false,
  lengthOfStay: lengthOfStay ?? "",
  emergencyContact: emergencyContact ?? "",
  emergencyContactRelationship: emergencyContactRelationship ?? "",
  emergencyName: emergencyName ?? "",
  dunedinResidentialAddress: dunedinResidentialAddress ?? "",
  studentId: studentId ?? "",
  customFields: customFields ?? [],
});

type ValidationSchemaProps = {
  namingFields?: any[];
  isR18?: boolean;
  eventDate?: string;
  venue: VenueAddressFragment;
};

export const validationSchema = ({
  namingFields = [],
  isR18 = false,
  eventDate = new Date().toString(),
  venue,
}: ValidationSchemaProps) => {
  const birthDateField = namingFields?.find(
    ({ field }) => field === "birthDate"
  );

  const validationShape = {
    firstName: namingFields?.find(({ field }) => field === "firstName")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    lastName: namingFields?.find(({ field }) => field === "lastName")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    email: namingFields?.find(({ field }) => field === "email")?.isRequired
      ? Yup.string().email().required()
      : Yup.string().nullable(),
    gender: namingFields?.find(({ field }) => field === "gender")?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    phoneNumber: namingFields?.find(({ field }) => field === "phoneNumber")
      ?.isRequired
      ? Yup.string()
          .matches(/^\+?\d+$/, "Invalid phone number")
          .required()
      : Yup.string().nullable(),
    birthDate: DOBDateInputSchema.test({
      name: "dob-date-test",
      test(value) {
        if (birthDateField?.isRequired && !value) {
          return this.createError({ message: "Date of birth is required" });
        }

        const dateMinR18 = subYears(new Date(eventDate), 18);

        if (birthDateField?.isRequired && isR18 && value > dateMinR18) {
          const eventDateZoned = formatEventDate(eventDate, "date", {
            timeZone: venue?.timezone,
            locale: venue?.locale,
          });

          return this.createError({
            message: `You need to be 18 years or older to attend this event when it starts on ${eventDateZoned}`,
          });
        }
        return true;
      },
    }),
    postalCode: namingFields?.find(({ field }) => field === "postalCode")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    addressLine1: namingFields?.find(({ field }) => field === "addressLine1")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    addressLine2: namingFields?.find(({ field }) => field === "addressLine2")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    addressLine3: namingFields?.find(({ field }) => field === "addressLine3")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    licensePlate: namingFields?.find(({ field }) => field === "licensePlate")
      ?.isRequired
      ? Yup.string().min(1).max(20).required()
      : Yup.string().nullable(),
    membershipNumber: namingFields?.find(
      ({ field }) => field === "membershipNumber"
    )?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    comments: namingFields?.find(({ field }) => field === "comments")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    dietaryRequirements: namingFields?.find(
      ({ field }) => field === "dietaryRequirements"
    )?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    companyName: namingFields?.find(({ field }) => field === "companyName")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    memberSince: namingFields?.find(({ field }) => field === "memberSince")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    usableForEmailMarketing: namingFields?.find(
      ({ field }) => field === "usableForEmailMarketing"
    )?.isRequired
      ? Yup.boolean().required()
      : Yup.boolean().nullable(),
    usableForSMSMarketing: namingFields?.find(
      ({ field }) => field === "usableForSMSMarketing"
    )?.isRequired
      ? Yup.boolean().required()
      : Yup.boolean().nullable(),
    lengthOfStay: namingFields?.find(({ field }) => field === "lengthOfStay")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    emergencyName: namingFields?.find(({ field }) => field === "emergencyName")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    emergencyContact: namingFields?.find(
      ({ field }) => field === "emergencyContact"
    )?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    emergencyContactRelationship: namingFields?.find(
      ({ field }) => field === "emergencyContactRelationship"
    )?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    dunedinResidentialAddress: namingFields?.find(
      ({ field }) => field === "dunedinResidentialAddress"
    )?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
    studentId: namingFields?.find(({ field }) => field === "studentId")
      ?.isRequired
      ? Yup.string().required()
      : Yup.string().nullable(),
  };

  namingFields
    .filter((n) => n.isCustom)
    .forEach((namingField) => {
      validationShape[namingField.field] = namingField.isRequired
        ? namingField.type === "customCheckbox"
          ? Yup.bool()
              .default(!!namingField.defaultValue)
              .oneOf([true], "This field is required")
          : Yup.string().required()
        : Yup.string().nullable();
    }, {});

  return Yup.object().shape(validationShape);
};
